import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Trophy, ArrowUpRight, Share2, Copy, Check } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { fetchPaymentStatus } from "@/lib/api";
import { money } from "@/lib/format";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const pid = params.get("pid");

    const [status, setStatus] = useState("processing"); // processing | active | timeout
    const [listing, setListing] = useState(null);
    const [progress, setProgress] = useState(8);
    const [copiedBadge, setCopiedBadge] = useState(false);
    const timer = useRef(null);
    const attempts = useRef(0);

    useEffect(() => {
        if (!pid) { setStatus("nopid"); return; }
        let cancelled = false;

        const poll = async () => {
            attempts.current += 1;
            setProgress((p) => Math.min(92, p + 7));
            try {
                const data = await fetchPaymentStatus(pid);
                if (cancelled) return;
                if (data.status === "succeeded" && data.listing) {
                    setListing(data.listing);
                    setProgress(100);
                    setStatus("active");
                    return;
                }
            } catch (e) { /* keep polling */ }
            if (attempts.current >= 40) {
                setStatus("timeout");
                return;
            }
            timer.current = setTimeout(poll, 2000);
        };
        poll();
        return () => { cancelled = true; if (timer.current) clearTimeout(timer.current); };
    }, [pid]);

    const shareOnX = () => {
        if (!listing) return;
        const text = `We just claimed rank #${listing.rank} on @outbidfit! 🚀 Check out the live leaderboard:`;
        const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent("https://outbid.fit")}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const shareOnLinkedIn = () => {
        const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://outbid.fit")}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyBadge = () => {
        if (!listing) return;
        const snippet = `[![Ranked #${listing.rank} on outbid.fit](https://outbid.fit/api/badge/${listing.id}.svg)](https://outbid.fit)`;
        navigator.clipboard.writeText(snippet);
        setCopiedBadge(true);
        toast.success("Embed badge markdown copied to clipboard!");
        setTimeout(() => setCopiedBadge(false), 3000);
    };

    return (
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-16">
            <Card className="bg-card border-border p-8 text-center" data-testid="payment-success-card">
                {status === "active" ? (
                    <>
                        <span className="grid place-items-center h-16 w-16 rounded-full bg-brand/15 text-brand mx-auto mb-4">
                            <CheckCircle2 className="h-9 w-9" />
                        </span>
                        <h1 className="font-heading text-2xl font-bold" data-testid="payment-success-status-text">You&apos;re live!</h1>
                        <p className="text-muted-foreground mt-2">Your bid is confirmed and your listing is now ranked.</p>
                        {listing ? (
                            <div className="mt-6 rounded-xl border border-brand/30 bg-brand/5 p-5 flex items-center justify-between">
                                <div className="text-left">
                                    <div className="font-medium">{listing.display_name}</div>
                                    {listing.specialty ? <div className="text-xs text-muted-foreground mt-0.5">{listing.specialty}</div> : null}
                                </div>
                                <div className="text-right">
                                    <div className="font-heading text-2xl font-bold text-brand">#{listing.rank}</div>
                                    <div className="text-xs text-muted-foreground tabular">{money(listing.bid_amount)}</div>
                                </div>
                            </div>
                        ) : null}

                        {/* Viral Share & Badge actions */}
                        <div className="mt-6 space-y-3 pt-4 border-t border-border text-left">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Share your rank</h3>
                            <div className="grid grid-cols-2 gap-2">
                                <Button
                                    variant="outline"
                                    onClick={shareOnX}
                                    className="w-full text-xs font-medium"
                                >
                                    <Share2 className="h-3.5 w-3.5 mr-1.5" /> Share on X
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={shareOnLinkedIn}
                                    className="w-full text-xs font-medium"
                                >
                                    <Share2 className="h-3.5 w-3.5 mr-1.5" /> LinkedIn
                                </Button>
                            </div>
                            <Button
                                variant="secondary"
                                onClick={copyBadge}
                                className="w-full text-xs font-medium mt-2"
                            >
                                {copiedBadge ? <Check className="h-3.5 w-3.5 mr-1.5 text-brand" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                                {copiedBadge ? "Badge Copied!" : "Copy README Embed Badge"}
                            </Button>
                        </div>

                        <Button data-testid="success-view-leaderboard-button" onClick={() => navigate("/")} className="mt-6 w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                            <Trophy className="h-4 w-4 mr-2" /> View the leaderboard
                        </Button>
                    </>
                ) : status === "timeout" ? (
                    <>
                        <span className="grid place-items-center h-16 w-16 rounded-full bg-secondary text-muted-foreground mx-auto mb-4">
                            <Loader2 className="h-9 w-9" />
                        </span>
                        <h1 className="font-heading text-2xl font-bold" data-testid="payment-success-status-text">Payment received</h1>
                        <p className="text-muted-foreground mt-2">Your listing is being activated — this can take a moment. It&apos;ll appear on the leaderboard shortly.</p>
                        <Button data-testid="success-view-leaderboard-button" onClick={() => navigate("/")} className="mt-6 w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                            View the leaderboard <ArrowUpRight className="h-4 w-4 ml-1" />
                        </Button>
                    </>
                ) : status === "nopid" ? (
                    <>
                        <span className="grid place-items-center h-16 w-16 rounded-full bg-brand/15 text-brand mx-auto mb-4">
                            <CheckCircle2 className="h-9 w-9" />
                        </span>
                        <h1 className="font-heading text-2xl font-bold" data-testid="payment-success-status-text">Payment received</h1>
                        <p className="text-muted-foreground mt-2">Thanks! Your listing will appear on the leaderboard once confirmed.</p>
                        <Button data-testid="success-view-leaderboard-button" onClick={() => navigate("/")} className="mt-6 w-full bg-brand text-primary-foreground hover:bg-brand-dim font-semibold">
                            View the leaderboard
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="grid place-items-center h-16 w-16 rounded-full bg-brand/15 text-brand mx-auto mb-4">
                            <Loader2 className="h-9 w-9 animate-spin" />
                        </span>
                        <h1 className="font-heading text-2xl font-bold" data-testid="payment-success-status-text">Payment received</h1>
                        <p className="text-muted-foreground mt-2">Activating your rank… hang tight.</p>
                        <div className="mt-6">
                            <Progress value={progress} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-2">Confirming payment with our processor</p>
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}
